import * as React from 'react'
import styled, { css } from 'styled-components'

import * as api from '@owl-nest/api-client/latest'
import * as hooks from '@owl-nest/hooks'
import { t } from '@owl-nest/localize'
import * as models from '@owl-nest/models'
import * as plume from '@ulule/owl-kit-components/next'

type Status = 'failed' | 'pending' | 'succeeded' | 'waiting'

export function StepBlock({ project }: { project: api.Project }): React.ReactElement | null {
  const properties = getStepBlockProperties(project)

  if (properties === undefined) {
    return null
  }

  return (
    <StepBlockWrapper>
      <plume.styles.heading.XXXXS>{t('Launch')}</plume.styles.heading.XXXXS>

      <StepWrapper>
        <plume.GlyphPastille status={properties.preparationStatus} />
        <StepTextProperties status={properties.preparationStatus} text={t('Preparation')} project={project} />
      </StepWrapper>

      <StepWrapper>
        <plume.GlyphPastille status={properties.rereadStatus} withBackground={properties.rereadStatus !== 'waiting'} />
        <StepTextProperties status={properties.rereadStatus} text={t('Review')} project={project} />
      </StepWrapper>

      <StepWrapper>
        <plume.GlyphPastille
          status={properties.publishingStatus}
          withBackground={properties.publishingStatus !== 'waiting'}
        />
        <StepTextProperties status={properties.publishingStatus} text={t('Go live')} project={project} />
      </StepWrapper>

      <a href={properties.href}>
        <plume.RoundButton>{properties.buttonLabel}</plume.RoundButton>
      </a>
    </StepBlockWrapper>
  )
}

function StepTextProperties({ status, text, project }: { status: Status; text: string; project: api.Project }) {
  return (
    <>
      {status === 'pending' ? (
        <plume.Link href={getLaunchUrl(project.id)}>
          <StepText $status={status}>{text}</StepText>
        </plume.Link>
      ) : (
        <StepText $status={status}>{text}</StepText>
      )}
    </>
  )
}

function getStepBlockProperties(project: api.Project):
  | {
      buttonLabel: string
      href: string
      preparationStatus: Status
      publishingStatus: Status
      rereadStatus: Status
    }
  | undefined {
  const isClientSide = hooks.useClientSide()
  const isOnDiscussionPage = isClientSide && window.location.pathname === getDiscussionUrl(project.id)

  switch (project.status) {
    case api.ProjectStatus.NEW:
      if (!models.project.isAllFieldsetsFilled(project)) {
        return {
          preparationStatus: 'pending',
          rereadStatus: 'waiting',
          publishingStatus: 'waiting',
          buttonLabel: t('Read the checklist'),
          href: getLaunchUrl(project.id),
        }
      } else
        return {
          preparationStatus: 'pending',
          rereadStatus: 'waiting',
          publishingStatus: 'waiting',
          buttonLabel: t('Submit for review'),
          href: getLaunchUrl(project.id),
        }
    case api.ProjectStatus.PENDING:
      return {
        preparationStatus: 'succeeded',
        rereadStatus: 'pending',
        publishingStatus: 'waiting',
        buttonLabel: t('Read our advices'),
        href: getLaunchUrl(project.id),
      }
    case api.ProjectStatus.PENDING_OWNER:
    case api.ProjectStatus.PENDING_FINAL_VALIDATION:
      if (project.moderation_message && !project.moderation_message.read_by_owner && !isOnDiscussionPage) {
        return {
          preparationStatus: 'succeeded',
          rereadStatus: 'pending',
          publishingStatus: 'waiting',
          buttonLabel: t('Read message'),
          href: getDiscussionUrl(project.id),
        }
      }
      return {
        preparationStatus: 'succeeded',
        rereadStatus: 'pending',
        publishingStatus: 'waiting',
        buttonLabel: t('Read our advices'),
        href: getLaunchUrl(project.id),
      }

    case api.ProjectStatus.VALIDATED:
      if (project.validation_message && !project.validation_message.read_by_owner && !isOnDiscussionPage) {
        return {
          preparationStatus: 'succeeded',
          rereadStatus: 'succeeded',
          publishingStatus: 'pending',
          buttonLabel: t('Read message'),
          href: getDiscussionUrl(project.id),
        }
      } else
        return {
          preparationStatus: 'succeeded',
          rereadStatus: 'succeeded',
          publishingStatus: 'pending',
          buttonLabel: t('Publish'),
          href: getLaunchUrl(project.id),
        }
    default:
      return
  }
}

function getLaunchUrl(id: number) {
  return `/projects/${id}/backoffice/launch/`
}

function getDiscussionUrl(id: number) {
  return `/projects/${id}/discussion/`
}

const StepText = styled(plume.styles.heading.XXXXXXS)<{ $status: Status }>`
  color: ${plume.COLORS.TEXT_SECONDARY};
  text-transform: uppercase;

  ${({ $status }) => {
    if ($status === 'succeeded') {
      return css`
        text-decoration: line-through;
      `
    }

    if ($status === 'pending') {
      return css`
        color: ${plume.COLORS.PRIMARY_GREY_900};
      `
    }
  }}
`

const StepBlockWrapper = styled.div`
  align-items: flex-start;
  box-shadow: ${plume.EFFECTS.SHADOW_1};
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  max-width: 60%;
  padding: 16px 8px;

  a {
    text-decoration: none;
    margin: 0 auto;
  }

  ${plume.RoundButton} {
    align-self: center;
    margin-top: 10px;
    white-space: nowrap;
  }

  > ${plume.styles.heading.XXXXS} {
    align-self: center;
    color: ${plume.COLORS.PRIMARY_BLACK};
    margin-bottom: 22px;
  }

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    max-width: 100%;

    a {
      max-width: 175px;

      ${plume.RoundButton} {
        width: 100%;
      }
    }
  }

  ${plume.styles.heading.XS} {
    margin-bottom: 12px;
  }
`

const StepWrapper = styled.div`
  align-items: center;
  display: flex;
  margin-bottom: 15px;
`
